/**
 * PublicScoreDisplay Component
 *
 * Displays SAT scores for public profiles with:
 * - Current and predicted scores per subject
 * - Trend indicators
 * - Confidence stars
 * - Optional goal progress bar
 *
 * Designed to be compelling for marketing - shows progress without exposing
 * sensitive internal metrics.
 */

import PropTypes from 'prop-types'
import ConfidenceStars from './ConfidenceStars'

const TREND_ICONS = {
  improving: { symbol: '\u2197', color: 'text-green-500', label: 'Improving' },
  stable: { symbol: '\u2192', color: 'text-gray-400', label: 'Stable' },
  declining: { symbol: '\u2198', color: 'text-red-500', label: 'Declining' },
  mixed_improving: { symbol: '\u2197', color: 'text-blue-500', label: 'Mixed (up)' },
  mixed_declining: { symbol: '\u2198', color: 'text-orange-500', label: 'Mixed (down)' }
}

const ScoreCard = ({
  label,
  currentScore,
  predictedScore,
  trend,
  confidence,
  accentColor = 'blue'
}) => {
  const trendInfo = TREND_ICONS[trend] || TREND_ICONS.stable
  const hasScores = currentScore || predictedScore

  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    amber: 'from-amber-500 to-orange-500'
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
      {/* Header with gradient */}
      <div className={`bg-gradient-to-r ${colorClasses[accentColor]} px-4 py-2`}>
        <h3 className="text-white font-semibold text-sm uppercase tracking-wide">{label}</h3>
      </div>

      <div className="p-4">
        {hasScores ? (
          <>
            {/* Current Score */}
            <div className="mb-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current</p>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {currentScore || predictedScore || '-'}
                </span>
                {trend && (
                  <span className={`text-lg ${trendInfo.color}`} title={trendInfo.label}>
                    {trendInfo.symbol}
                  </span>
                )}
              </div>
            </div>

            {/* Predicted Score */}
            {predictedScore && predictedScore !== currentScore && (
              <div className="mb-3 pt-2 border-t dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Predicted</p>
                <span className="text-xl font-semibold text-gray-700 dark:text-gray-200">
                  {predictedScore}
                </span>
              </div>
            )}

            {/* Confidence indicator */}
            {confidence && confidence.stars > 0 && (
              <div className="mt-2">
                <ConfidenceStars confidence={confidence} size="sm" showLabel={true} />
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-4 text-gray-400 dark:text-gray-500">
            <p className="text-sm">No data yet</p>
          </div>
        )}
      </div>
    </div>
  )
}

ScoreCard.propTypes = {
  label: PropTypes.string.isRequired,
  currentScore: PropTypes.number,
  predictedScore: PropTypes.number,
  trend: PropTypes.string,
  confidence: PropTypes.object,
  accentColor: PropTypes.oneOf(['blue', 'purple', 'amber'])
}

const PublicScoreDisplay = ({ scores = null, goal = null, showGoalProgress = true }) => {
  // If no scores data at all
  if (!scores) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <p>No score data available yet</p>
      </div>
    )
  }

  const { math, readingWriting, combined, hasData, totalChallenges } = scores

  // If user hasn't completed any challenges, show CTA
  if (!hasData && totalChallenges === 0) {
    return (
      <div className="bg-gradient-to-br from-primary-50 to-accent-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 text-center">
        <svg
          className="w-12 h-12 mx-auto text-primary-500 mb-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
          No Challenges Completed Yet
        </h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          Complete challenges to see predicted SAT scores
        </p>
      </div>
    )
  }

  // Calculate goal progress percentage
  const goalProgress =
    goal && combined.predictedScore
      ? Math.min(100, Math.round((combined.predictedScore / goal) * 100))
      : null

  return (
    <div className="space-y-4">
      {/* Score Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ScoreCard
          label="Math"
          currentScore={math.currentScore}
          predictedScore={math.predictedScore}
          trend={math.trend}
          confidence={math.confidence}
          accentColor="blue"
        />
        <ScoreCard
          label="Reading & Writing"
          currentScore={readingWriting.currentScore}
          predictedScore={readingWriting.predictedScore}
          trend={readingWriting.trend}
          confidence={readingWriting.confidence}
          accentColor="purple"
        />
        <ScoreCard
          label="Total"
          currentScore={combined.currentScore}
          predictedScore={combined.predictedScore}
          trend={combined.trend}
          accentColor="amber"
        />
      </div>

      {/* Goal Progress Bar */}
      {showGoalProgress && goal && goalProgress !== null && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              Goal: {goal}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">{goalProgress}% there</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-500"
              style={{ width: `${goalProgress}%` }}
            />
          </div>
          {combined.predictedScore && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              {goal - combined.predictedScore > 0
                ? `${goal - combined.predictedScore} points to go!`
                : 'Goal achieved!'}
            </p>
          )}
        </div>
      )}

      {/* Challenge Count Subtitle */}
      {totalChallenges > 0 && (
        <p className="text-center text-xs text-gray-500 dark:text-gray-400">
          Based on {totalChallenges} challenge{totalChallenges !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  )
}

PublicScoreDisplay.propTypes = {
  /** Scores data from API */
  scores: PropTypes.shape({
    math: PropTypes.shape({
      currentScore: PropTypes.number,
      predictedScore: PropTypes.number,
      trend: PropTypes.string,
      confidence: PropTypes.object
    }),
    readingWriting: PropTypes.shape({
      currentScore: PropTypes.number,
      predictedScore: PropTypes.number,
      trend: PropTypes.string,
      confidence: PropTypes.object
    }),
    combined: PropTypes.shape({
      currentScore: PropTypes.number,
      predictedScore: PropTypes.number,
      trend: PropTypes.string
    }),
    hasData: PropTypes.bool,
    totalChallenges: PropTypes.number
  }),
  /** User's goal SAT score */
  goal: PropTypes.number,
  /** Whether to show the goal progress bar */
  showGoalProgress: PropTypes.bool
}

export default PublicScoreDisplay
